import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './RetroBoard.css'
import RetroColumn from './RetroColumn'
import {
  fetchRetroState,
  retroStreamUrl,
  retroJoinUrl,
  addRetroCard,
  removeRetroCard,
  setRetroCardReaction,
  addRetroComment,
  removeRetroComment,
  setRetroCardPinned,
} from './utils/retroApiClient'
import { loadReactedIds, saveReactedIds } from './utils/retroReactions'
import { getVisitorId } from './utils/retroVisitor'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const COLUMNS = [
  { key: 'good', title: 'O que foi bom' },
  { key: 'bad', title: 'O que foi ruim' },
  { key: 'improve', title: 'O que melhorar' },
]

const RetroBoardMaster = () => {
  useDocumentMeta({
    title: 'Retro ao Vivo, Mestre do Quadro | jarder.dev',
    description: 'Acompanhe os cartões chegando ao vivo e modere o quadro se precisar.',
  })

  const { id } = useParams()
  const masterToken = (/token=([^&]+)/.exec(window.location.hash) || [])[1] || ''

  const [state, setState] = useState(null)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [reactedIds, setReactedIds] = useState(() => loadReactedIds(id))

  const joinUrl = retroJoinUrl(id)

  useEffect(() => {
    let cancelled = false

    fetchRetroState(id)
      .then((data) => {
        if (cancelled) return
        setState(data)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })

    const eventSource = new EventSource(retroStreamUrl(id))
    eventSource.onmessage = (event) => {
      setState(JSON.parse(event.data))
    }

    return () => {
      cancelled = true
      eventSource.close()
    }
  }, [id])

  const addCard = async (column, text) => {
    setError('')
    try {
      await addRetroCard(id, column, text)
      trackEvent('tool_generate', { tool: 'retro_board_add_card', column })
    } catch (err) {
      setError(err.message || 'Não foi possível enviar o cartão.')
    }
  }

  const removeCard = async (card) => {
    setError('')
    try {
      await removeRetroCard(id, card.id, { masterToken })
      trackEvent('tool_generate', { tool: 'retro_board_remove_card' })
    } catch (err) {
      setError(err.message || 'Não foi possível excluir o cartão.')
    }
  }

  const canRemove = () => true

  const toggleReact = async (card) => {
    setError('')
    const alreadyReacted = reactedIds.has(card.id)
    try {
      await setRetroCardReaction(id, card.id, getVisitorId(), !alreadyReacted)

      setReactedIds((current) => {
        const next = new Set(current)
        if (alreadyReacted) next.delete(card.id)
        else next.add(card.id)
        saveReactedIds(id, next)
        return next
      })
      trackEvent('tool_generate', { tool: 'retro_board_react' })
    } catch (err) {
      setError(err.message || 'Não foi possível reagir.')
    }
  }

  const comment = async (card, text) => {
    setError('')
    try {
      await addRetroComment(id, card.id, text)
      trackEvent('tool_generate', { tool: 'retro_board_comment' })
    } catch (err) {
      setError(err.message || 'Não foi possível comentar.')
    }
  }

  const removeComment = async (card, commentId) => {
    setError('')
    try {
      await removeRetroComment(id, card.id, commentId, { masterToken })
      trackEvent('tool_generate', { tool: 'retro_board_remove_comment' })
    } catch (err) {
      setError(err.message || 'Não foi possível excluir o comentário.')
    }
  }

  const canRemoveComment = () => true

  const togglePin = async (card) => {
    setError('')
    try {
      await setRetroCardPinned(id, card.id, masterToken, !card.pinned)
      trackEvent('tool_generate', { tool: 'retro_board_pin', pinned: !card.pinned })
    } catch (err) {
      setError(err.message || 'Não foi possível fixar o cartão.')
    }
  }

  const copyJoinLink = () => {
    navigator.clipboard.writeText(joinUrl)
    setCopied(true)
    trackEvent('tool_copy', { tool: 'retro_board' })
  }

  if (status === 'loading') {
    return (
      <div className="tool-page">
        <p>Carregando...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Quadro não encontrado</h1>
        <p className="tool-disclaimer">Esse quadro não existe ou expirou.</p>
        <Link to="/ferramentas/retro-ao-vivo" className="tool-button">
          Criar um novo quadro
        </Link>
      </div>
    )
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Retro ao Vivo, Mestre do Quadro</h1>
      <p className="tool-disclaimer">
        Essa tela é só sua. Compartilhe o link abaixo com o grupo. Os
        cartões são anônimos, você pode excluir, fixar e moderar
        comentários, mas não ver quem escreveu.
      </p>

      <div className="tool-result-card">
        <span className="tool-result-label">Link pro grupo</span>
        <span className="tool-result-value" style={{ wordBreak: 'break-all' }}>
          {joinUrl}
        </span>
        <div className="tool-result-actions">
          <button type="button" className="tool-button tool-button-secondary" onClick={copyJoinLink}>
            {copied ? 'Copiado!' : 'Copiar link'}
          </button>
        </div>
      </div>

      {error && <p className="tool-error">{error}</p>}

      <div className="retro-board">
        {COLUMNS.map((col) => (
          <RetroColumn
            key={col.key}
            title={col.title}
            column={col.key}
            cards={state.cards.filter((card) => card.column === col.key)}
            onAdd={addCard}
            canRemove={canRemove}
            onRemove={removeCard}
            reactedIds={reactedIds}
            onToggleReact={toggleReact}
            onComment={comment}
            isMaster
            onTogglePin={togglePin}
            canRemoveComment={canRemoveComment}
            onRemoveComment={removeComment}
          />
        ))}
      </div>
    </div>
  )
}

export default RetroBoardMaster
